/**
 * Где нас называют, а где нет — списком вопросов.
 *
 * Плитка «видимость 21%» отвечает на «сколько». Работать можно только с «где»:
 * клиент приходит с готовым вопросом «по каким запросам меня не видно», и
 * ответом на него служит список, а не доля.
 *
 * Экран открывается с уже выставленным отбором «не называет никто» и худшими
 * сверху. Пустой отбор заставил бы искать нужное самому.
 *
 * Клетки читаются по ОДНОМУ замеру — так же, как матрица на «Движках»
 * (`ui/cells.ts`), и по той же причине: попытки разных недель, сложенные в одну
 * клетку, дают состояние, которого не было ни в одну из них.
 */

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { requestWith } from '../api/client';
import { useRead } from '../ui/query';
import { WithBrand } from '../ui/screen';
import { SelectField, TextField } from '../ui/fields';
import { byEngineOrder, engineTitle, engineTitles } from '../ui/controls';
import { Empty, ErrorNotice, Skeleton } from '../ui/state';
import { Matrix, Stamp } from '../ui/viz';
import type { CellState, MatrixRow } from '../ui/viz';
import { FUNNEL_ORDER, funnelStage } from '../ui/labels';
import {
  countOf,
  dateProse,
  int,
  NOT_MEASURED,
  percent,
  position,
} from '@shared/src/format';
import type { components } from '../api/schema';

type Engine = components['schemas']['EngineWithHealth'];
type FunnelStage = components['schemas']['FunnelStage'];
type VisibilityPage = components['schemas']['QuestionVisibilityPage'];
type Question = components['schemas']['QuestionVisibility'];
type Cell = components['schemas']['QuestionVisibilityCell'];
type State = components['schemas']['QuestionVisibilityState'];

/** Строк на страницу. Потолок страницы договора — 200, столько экран не покажет. */
const PAGE = 50;

/**
 * Состояния строки словами.
 *
 * «Не измерено» стоит отдельно от нуля намеренно: ноль означает «спросили и не
 * назвали», а пропуск — «ответа не пришло». Один цвет на двоих показывал бы
 * работу там, где её нет.
 */
const STATE_WORD: Record<State, string> = {
  unseen: 'Не называет никто',
  partial: 'Называют не все',
  named: 'Называют все движки',
  unmeasured: NOT_MEASURED,
};

const STATE_ORDER: readonly State[] = ['unseen', 'partial', 'named', 'unmeasured'];

interface Filters {
  state: State | '';
  engine: string;
  cluster: string;
  stage: FunnelStage | '';
  search: string;
}

/** Экран открывается на дырах: за ними и приходят. */
const START: Filters = {
  state: 'unseen',
  engine: '',
  cluster: '',
  stage: '',
  search: '',
};
const NONE: Filters = { state: '', engine: '', cluster: '', stage: '', search: '' };

/** Состояние из адреса: с обзора бренда сюда ведут числа, каждое в свой отбор. */
function asked(value: string | null): State | '' {
  return STATE_ORDER.find((state) => state === value) ?? START.state;
}

export function Visibility() {
  // Отбор из адреса читается на заводе состояния, а `key` заставляет экран
  // завестись заново, когда адрес сменился: без этого «назад» между двумя
  // ссылками с обзора бренда оставляло бы прежний отбор под новым адресом.
  const [params] = useSearchParams();
  const asked = params.get('state') ?? '';
  return (
    <WithBrand title="Видимость по вопросам">
      {(brandId) => <Body key={`${brandId}:${asked}`} brandId={brandId} />}
    </WithBrand>
  );
}

function Body({ brandId }: { brandId: string }) {
  // Адрес читается один раз, при заводе состояния: дальше отбором управляет
  // человек, и переписывать его выбор строкой адреса нельзя.
  const [params] = useSearchParams();
  const [filters, setFilters] = useState<Filters>({
    ...START,
    state: asked(params.get('state')),
  });
  const [limit, setLimit] = useState<number>(PAGE);

  const engines = useRead<Engine[]>('/engines');
  // Отбор лежит в запросе целиком, а не досеивается на экране: считать состояния
  // и сводку обязан тот, кто знает весь замер, иначе числа над списком разошлись
  // бы со списком на второй же странице.
  const query = {
    ...(filters.state ? { state: filters.state } : {}),
    ...(filters.engine ? { engine_code: filters.engine } : {}),
    ...(filters.cluster ? { cluster_key: filters.cluster } : {}),
    ...(filters.stage ? { funnel_stage: filters.stage } : {}),
    ...(filters.search.trim() ? { q: filters.search.trim() } : {}),
  };
  const first = useRead<VisibilityPage>(`/brands/${brandId}/questions/visibility`, {
    ...query,
    limit,
  });

  const catalogue = engines.kind === 'ready' ? engines.data : [];
  const titles = engineTitles(catalogue);
  const order = byEngineOrder(catalogue);

  if (first.kind === 'failed') return <ErrorNotice error={first.error} />;
  if (first.kind === 'loading') return <Skeleton rows={4} />;

  const page = first.data;
  const codes = [...page.engines].sort(order);
  const chosen = filters.state !== '' || filters.engine !== '' || filters.cluster !== '';
  const narrowed = chosen || filters.stage !== '' || filters.search.trim() !== '';

  if (!page.run_id) {
    return (
      <Empty eyebrow="Замера ещё не было" title="Список появится после первого замера">
        Мы спросим движки по всем вопросам ядра. После этого здесь встанет список.
      </Empty>
    );
  }

  return (
    <>
      <Stamp>
        {page.snapshot_date
          ? `Замер от ${dateProse(page.snapshot_date)} · вопросов ${int(page.summary.questions_total)}`
          : `Вопросов ${int(page.summary.questions_total)}`}
      </Stamp>

      {/* Незакрытая сетка — первоклассное состояние, а не подпись мелким:
          список читается как итог замера, и человек уносит его подрядчику. */}
      {page.snapshot_date ? null : (
        <div className="notice warn" role="status">
          <b>Сетка ещё не закрыта</b>
          Замер идёт: часть клеток дозаполнится, и состояния вопросов ещё изменятся. Числа
          посчитаны по тому, что собрано.
        </div>
      )}

      <p className="lead">{leadOf(page.summary, filters.engine, titles)}</p>

      <Filters
        value={filters}
        onChange={(next) => {
          setFilters(next);
          setLimit(PAGE);
        }}
        codes={codes}
        titles={titles}
        clusters={page.clusters}
        narrowed={narrowed}
      />

      <Rows
        brandId={brandId}
        query={query}
        page={page}
        limit={limit}
        onLimit={setLimit}
        codes={codes}
        titles={titles}
        narrowed={narrowed}
        onDrop={() => {
          setFilters(NONE);
          setLimit(PAGE);
        }}
      />
    </>
  );
}

/** Первая строка экрана: тот же вопрос, с которым клиент пришёл, и ответ на него. */
function leadOf(
  summary: VisibilityPage['summary'],
  engine: string,
  titles: Map<string, string>,
): string {
  const total = summary.questions_total;
  const whose = engine ? ` движок ${engineTitle(titles, engine)}` : ' ни один движок';
  if (total === 0) return 'В этом замере не было ни одного вопроса.';
  if (summary.unseen === 0) {
    return `Из ${countOf(total, 'вопроса', 'вопросов', 'вопросов')}${whose} не пропустил ни одного.`;
  }
  return `Из ${countOf(total, 'вопроса', 'вопросов', 'вопросов')}${whose} не называет вас по ${int(summary.unseen)}.`;
}

/* ──────────────────────────────── отбор ───────────────────────────────────── */

function Filters({
  value,
  onChange,
  codes,
  titles,
  clusters,
  narrowed,
}: {
  value: Filters;
  onChange: (value: Filters) => void;
  codes: string[];
  titles: Map<string, string>;
  clusters: VisibilityPage['clusters'];
  narrowed: boolean;
}) {
  return (
    <div className="filters">
      <SelectField<State | ''>
        label="Состояние"
        value={value.state}
        onChange={(state) => onChange({ ...value, state })}
        options={[
          { value: '', label: 'Любое' },
          ...STATE_ORDER.map((state) => ({ value: state, label: STATE_WORD[state] })),
        ]}
      />
      <SelectField
        label="Движок"
        value={value.engine}
        onChange={(engine) => onChange({ ...value, engine })}
        hint="Состояние считается по выбранному движку"
        options={[
          { value: '', label: 'Все' },
          ...codes.map((code) => ({ value: code, label: engineTitle(titles, code) })),
        ]}
      />
      <SelectField
        label="Тема"
        value={value.cluster}
        onChange={(cluster) => onChange({ ...value, cluster })}
        options={[
          { value: '', label: 'Любая' },
          ...clusters
            .filter((cluster) => cluster.cluster_key)
            .map((cluster) => ({
              value: cluster.cluster_key ?? '',
              label: `${cluster.cluster_label ?? cluster.cluster_key ?? ''} · ${int(cluster.questions)}`,
            })),
        ]}
      />
      <SelectField<FunnelStage | ''>
        label="Стадия воронки"
        value={value.stage}
        onChange={(stage) => onChange({ ...value, stage })}
        options={[
          { value: '', label: 'Любая' },
          ...FUNNEL_ORDER.map((stage) => ({ value: stage, label: funnelStage(stage) })),
        ]}
      />
      <TextField
        label="Поиск"
        value={value.search}
        onChange={(search) => onChange({ ...value, search })}
        optional
        placeholder="часть вопроса"
      />
      {narrowed ? (
        <button type="button" className="linkish" onClick={() => onChange(NONE)}>
          Сбросить отбор
        </button>
      ) : null}
    </div>
  );
}

/* ──────────────────────────────── строки ──────────────────────────────────── */

/**
 * Сколько строк экран готов показать разом. Потолок страницы договора — 200, и
 * это же потолок таблицы: список длиннее читают не глазами, а файлом.
 */
const STEPS = [50, 100, 200] as const;

/** Предохранитель выгрузки: пять тысяч строк, дальше отбор сузят. */
const EXPORT_PAGES = 25;

function Rows({
  brandId,
  query,
  page,
  limit,
  onLimit,
  codes,
  titles,
  narrowed,
  onDrop,
}: {
  brandId: string;
  query: Record<string, string | number>;
  page: VisibilityPage;
  limit: number;
  onLimit: (limit: number) => void;
  codes: string[];
  titles: Map<string, string>;
  narrowed: boolean;
  onDrop: () => void;
}) {
  const next = STEPS.find((step) => step > limit);

  if (page.items.length === 0) {
    return (
      <Empty
        eyebrow="Ничего не нашлось"
        title="Под эти условия вопросов нет"
        action={
          narrowed ? (
            <button className="btn btn-ghost" type="button" onClick={onDrop}>
              Сбросить отбор
            </button>
          ) : undefined
        }
      >
        Вопросы в замере есть, но ни один не подходит под выбранные условия.
      </Empty>
    );
  }

  return (
    <>
      <QuestionTable items={page.items} codes={codes} titles={titles} />

      <div className="row-actions">
        {page.next_cursor && next ? (
          <button type="button" className="btn btn-ghost" onClick={() => onLimit(next)}>
            Показать ещё
          </button>
        ) : null}
        <ExportButton brandId={brandId} query={query} codes={codes} titles={titles} />
      </div>

      {/* Обрезанный список обязан назвать себя обрезанным. Молча укороченная
          таблица читается как весь ответ на вопрос «где нас не видно». */}
      {page.next_cursor && !next ? (
        <Stamp>
          Показаны первые {int(page.items.length)} строк. Сузьте отбор или скачайте
          список.
        </Stamp>
      ) : null}
    </>
  );
}

/**
 * Выгрузка отобранного файлом.
 *
 * Список из полутора сотен вопросов отдают подрядчику целиком, а не экраном.
 * Страницы дочитываются курсором до конца: файл, обрезанный на первой странице,
 * хуже отсутствующего — по нему сделают работу и решат, что закрыли всё.
 */
function ExportButton({
  brandId,
  query,
  codes,
  titles,
}: {
  brandId: string;
  query: Record<string, string | number>;
  codes: string[];
  titles: Map<string, string>;
}) {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  /** Сколько строк ушло в файл, если предохранитель сработал и файл неполон. */
  const [cut, setCut] = useState<number | null>(null);

  const save = async () => {
    setBusy(true);
    setFailure(null);
    setCut(null);
    try {
      const rows: Question[] = [];
      let cursor: string | null = null;
      for (let page = 0; page < EXPORT_PAGES; page += 1) {
        const answer: { body: VisibilityPage } = await requestWith<VisibilityPage>(
          `/brands/${brandId}/questions/visibility`,
          { query: { ...query, limit: 200, ...(cursor ? { cursor } : {}) } },
        );
        rows.push(...answer.body.items);
        cursor = answer.body.next_cursor ?? null;
        if (!cursor) break;
      }
      download(csvOf(rows, codes, titles));
      // Файл, обрезанный предохранителем, обязан назвать себя обрезанным: по
      // нему сделают работу и решат, что закрыли всё.
      if (cursor) setCut(rows.length);
    } catch (error) {
      // Отказ показывается словами сервера: он знает и причину, и сроки.
      // Своё «попробуйте ещё раз» вместо них отправляет человека жать кнопку
      // по кругу там, где кончилась квота или нет прав.
      setFailure(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button type="button" className="btn btn-ghost" onClick={save} disabled={busy}>
        {busy ? 'Собираем список' : 'Скачать список'}
      </button>
      {failure ? <ErrorNotice error={failure} /> : null}
      {cut !== null ? (
        <Stamp>
          В файл ушли первые {int(cut)} строк: под этот отбор попало больше. Сузьте отбор,
          чтобы забрать остальные.
        </Stamp>
      ) : null}
    </>
  );
}

/** Таблица для подрядчика: строка на вопрос, колонка на движок. */
function csvOf(rows: Question[], codes: string[], titles: Map<string, string>): string {
  const head = [
    'Вопрос',
    'Тема',
    'Стадия',
    'Состояние',
    'Названо движков',
    'Отвечало движков',
    'Видимость, %',
    'Среднее место',
    'Называют вместо вас',
    ...codes.map((code) => engineTitle(titles, code)),
  ];
  const body = rows.map((item) => [
    item.question_text,
    item.cluster_label ?? '',
    funnelStage(item.funnel_stage),
    STATE_WORD[item.state],
    String(item.engines_named),
    String(item.engines_counted),
    item.visibility === null || item.visibility === undefined
      ? ''
      : String(item.visibility),
    item.avg_position === null || item.avg_position === undefined
      ? ''
      : String(item.avg_position),
    item.rivals.map((rival) => rival.name).join(' · '),
    ...codes.map(
      (code) => cellWord(item.cells.find((one) => one.engine_code === code)) ?? '',
    ),
  ]);
  return [head, ...body].map((line) => line.map(quote).join(';')).join('\r\n');
}

/** Кавычки по RFC 4180: точка с запятой и перевод строки внутри поля — обычное дело. */
function quote(value: string): string {
  return `"${value.replaceAll('"', '""')}"`;
}

function download(csv: string): void {
  // Метка порядка байтов нужна табличным редакторам: без неё кириллица в них
  // открывается кракозябрами, и клиент решает, что сломан файл.
  const file = new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(file);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'vidimost-po-voprosam.csv';
  link.click();
  URL.revokeObjectURL(url);
}

function QuestionTable({
  items,
  codes,
  titles,
}: {
  items: Question[];
  codes: string[];
  titles: Map<string, string>;
}) {
  const rows: MatrixRow[] = items.map((item) => ({
    key: item.question_id,
    label: item.question_text,
    cells: codes.map((code) => {
      const cell = item.cells.find((one) => one.engine_code === code);
      return { key: code, state: stateOf(cell), word: cellWord(cell) };
    }),
  }));

  return (
    <>
      <div style={{ ['--cols' as string]: String(codes.length) }}>
        <Matrix
          columns={codes.map((code) => ({ key: code, name: engineTitle(titles, code) }))}
          rows={rows}
          caption={`Строк на экране: ${int(items.length)}`}
        />
      </div>

      <div className="tbl-wrap">
        <table className="mini">
          <caption>Кого называют вместо вас и на каком месте стоите вы</caption>
          <thead>
            <tr>
              <th scope="col">Вопрос</th>
              <th scope="col">Движки</th>
              <th scope="col" className="tnum-col">
                Видимость
              </th>
              <th scope="col" className="tnum-col">
                Место
              </th>
              <th scope="col">Называют вместо вас</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.question_id}>
                <td>{item.question_text}</td>
                <td>
                  {item.engines_counted === 0
                    ? NOT_MEASURED
                    : `${int(item.engines_named)} из ${int(item.engines_counted)}`}
                </td>
                <td className="tnum">
                  {item.visibility === null || item.visibility === undefined
                    ? NOT_MEASURED
                    : percent(item.visibility, 1)}
                </td>
                <td className="tnum">
                  {item.avg_position === null || item.avg_position === undefined
                    ? '—'
                    : position(item.avg_position)}
                </td>
                <td>
                  {item.rivals.length === 0
                    ? '—'
                    : item.rivals.map((rival) => rival.name).join(', ')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

/** Состояние клетки в словарь матрицы «Движков»: он один на весь кабинет. */
function stateOf(cell: Cell | undefined): CellState {
  if (!cell || cell.tries === 0) return 'na';
  if (cell.hits === 0) return 'none';
  return cell.hits >= cell.tries ? 'both' : 'one';
}

function cellWord(cell: Cell | undefined): string | undefined {
  if (!cell || cell.tries === 0) return undefined;
  if (cell.hits === 0) return 'не назван';
  if (cell.hits >= cell.tries) return `назван во всех ${int(cell.tries)}`;
  return `назван в ${int(cell.hits)} из ${int(cell.tries)}`;
}
